import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable, observable } from 'mobx';
import { FileRejection } from 'react-dropzone/.';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { INotificationsService } from '../notifications/services/notifications.service';
import { IDatasetDraft, IDatasetDraftsStore } from './datasetDrafts.store';
import { isAllowedImageType, isAllowedSize, isNotDuplicatedExtension, toMBytes } from './helpers/image.helpers';
import { IImageFile, IImagesUploaderService } from './imagesUpload.service';
import { IRejectedFile } from './models/IRejectedFile';
import { RejectStatus } from './models/RejectStatus';

export interface ICreateDatasetFilesService {
  addImagesToDraft(images: File[], fileRejections: FileRejection[]): Promise<void>;
  cleanImagesFromFile(): void;
  isProcessingImages: boolean;
}

export namespace ICreateDatasetFilesService {
  export const $: interfaces.ServiceIdentifier<ICreateDatasetFilesService> = Symbol('CREATE_DATASET_FILES_SERVICE');
}

@injectable()
export class CreateDatasetFilesService implements ICreateDatasetFilesService {
  private imageProcessingCount: number = 0;
  private acceptedImagesPlaceholder: IImageFile[] = [];
  private rejectedImagesPlaceholder: IRejectedFile[] = [];

  constructor(
    @inject(IDatasetDraftsStore.$) private readonly datasetsDraftStore: IDatasetDraftsStore,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(IImagesUploaderService.$) private readonly imagesUploaderService: IImagesUploaderService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore
  ) {
    makeObservable(this);
  }

  @observable
  isProcessingImages: boolean = false;

  @action.bound
  async addImagesToDraft(images: File[]): Promise<void> {
    const draft = this.datasetsDraftStore.draft;
    this.startProcessingImagesFiles(draft);
    const acceptedImages: IImageFile[] = [];
    const rejectedImages: IRejectedFile[] = [];

    const duplicateNames = await this.imagesUploaderService.getDuplicatedImageNamesAsync(
      draft.id,
      images.map((x) => x.name).concat(this.acceptedImagesPlaceholder.map((x) => x.file.name))
    );

    for (const file of images) {
      if (duplicateNames.includes(file.name) || this.acceptedImagesPlaceholder.concat(acceptedImages).some((x) => x.file.name === file.name)) {
        rejectedImages.push({ file, status: RejectStatus.DUPLICATED_NAME });
        continue;
      }
      if (!isAllowedImageType(file.name)) {
        rejectedImages.push({ file, status: RejectStatus.TYPE });
        continue;
      }
      if (!isNotDuplicatedExtension(file.name)) {
        rejectedImages.push({ file, status: RejectStatus.DUPLICATED_EXTENSION });
        continue;
      }
      if (!isAllowedSize(file)) {
        rejectedImages.push({ file, status: RejectStatus.SIZE });
        continue;
      }
      acceptedImages.push({ file });
    }

    await this.endProcessingImagesFilesAsync(acceptedImages, rejectedImages, draft);
  }

  @action.bound
  cleanImagesFromFile(): void {
    const draft = this.datasetsDraftStore.draft;
    draft.imagesToUploadFromFile = [];
    draft.imagesRejectedFromFile = [];
    draft.imagePreValidationWasSuccessful = true;
    this.imageProcessingCount = 0;
    this.acceptedImagesPlaceholder = [];
    this.rejectedImagesPlaceholder = [];
    this.isProcessingImages = false;
  }

  @action.bound
  startProcessingImagesFiles(draft: IDatasetDraft): void {
    this.imageProcessingCount += 1;
    this.isProcessingImages = this.imageProcessingCount > 0;
    if (this.imageProcessingCount <= 1) {
      this.acceptedImagesPlaceholder = [...draft.imagesToUploadFromFile];
      this.rejectedImagesPlaceholder = [...draft.imagesRejectedFromFile];
    }
  }

  @action.bound
  async endProcessingImagesFilesAsync(acceptedImages: IImageFile[], rejectedImages: IRejectedFile[], draft: IDatasetDraft): Promise<void> {
    this.imageProcessingCount = this.imageProcessingCount > 0 ? this.imageProcessingCount - 1 : 0;
    this.acceptedImagesPlaceholder.push(...acceptedImages);
    this.rejectedImagesPlaceholder.push(...rejectedImages);

    const willExceeds = await this.imagesUploaderService.willExceedsImageLimit(
      this.currentWorkspaceStore.currentWorkspace.id,
      this.acceptedImagesPlaceholder.length,
      toMBytes(this.acceptedImagesPlaceholder.reduce((sum, current) => sum + current.file.size, 0))
    );

    if (this.imageProcessingCount <= 0) {
      draft.imagesToUploadFromFile = this.acceptedImagesPlaceholder;
      draft.imagesRejectedFromFile = this.rejectedImagesPlaceholder;
    }

    draft.willExceedsUploadImageSizeLimit = willExceeds.willExceedsAvailableSpace;
    draft.imagePreValidationWasSuccessful = willExceeds.areAllSuccessful;
    draft.ownerPlan = willExceeds.ownerPlan;
    this.isProcessingImages = false;
  }
}
